<script setup>
    import { ref } from 'vue'
    import { MAX_FACE_IMAGES } from '../utils/faceImages.js'

    // 顔画像スロット [{ file: File|null, previewImg: string }, ...]
    const props = defineProps({
        modelValue: { type: Array, required: true }
    })
    const emit = defineEmits(['update:modelValue'])

    // 指定スロットの画像を差し替えて親へ通知
    const updateImage = (index, image) => {
        const newImages = [...props.modelValue]
        newImages[index] = image
        emit('update:modelValue', newImages)
    }

    // スロットごとのファイル選択input
    const fileInputRefs = ref([])

    // ファイル設定
    const setImageFile = (index, file) => {
        if (!file) return

        // JPG/JPEG、PNG、WEBPのみ許可
        const validTypes = [
            'image/jpeg',
            'image/png',
            'image/jpg',
            'image/webp'
        ]
        if (!validTypes.includes(file.type)) {
            alert('JPG、PNG、WEBP形式の画像を選択してください。');
            return;
        }

        if (props.modelValue[index].previewImg) {
            URL.revokeObjectURL(props.modelValue[index].previewImg)
        }
        updateImage(index, { file, previewImg: URL.createObjectURL(file) })
    }
    // ファイル選択ダイアログを開く
    const openFileDialog = (index) => {
        fileInputRefs.value[index]?.click()
    }
    // ファイル選択時の処理
    const uploadFile = (index, event) => {
        setImageFile(index, event.target.files?.[0])
        // 同じファイルを再選択できるよう選択状態をクリア
        event.target.value = ''
    }
    // ファイルドロップ時の処理
    const dropFile = (index, event) => {
        setImageFile(index, event.dataTransfer?.files?.[0])
    }
    // 画像削除
    const deleteImage = (index) => {
        if (props.modelValue[index].previewImg) {
            URL.revokeObjectURL(props.modelValue[index].previewImg)
        }
        updateImage(index, { file: null, previewImg: '' })
    }
</script>

<template>
    <div class="imgSlotList">
        <div
            v-for="(image, index) in modelValue.slice(0, MAX_FACE_IMAGES)"
            :key="index"
            class="imgSlot"
            @dragover.prevent
            @drop.prevent="dropFile(index, $event)"
        >
            <div class="imgSlotHeader">
                <span class="imgSlotTitle">画像 {{ index + 1 }}</span>
                <span v-if="index === 0" class="slotRequireView">必須</span>
                <span v-else class="optionalView">任意</span>
            </div>
            <input
                type="file"
                :ref="el => fileInputRefs[index] = el"
                @change="uploadFile(index, $event)"
                accept=".jpg,.jpeg,.png,.webp"
                hidden
            >
            <!-- 画像選択済み：プレビュー表示 -->
            <div v-if="image.previewImg" class="imgSlotPreview" @click="openFileDialog(index)">
                <img :src="image.previewImg" alt="">
            </div>
            <!-- 未選択：アップロードエリア表示 -->
            <div v-else class="dropArea" @click="openFileDialog(index)">
                <div class="dropInner">
                    <img src="../assets/userregist_img.png" alt="">
                    <p class="mainText">画像をアップロードしてください</p>
                    <p class="subText">またはドラッグ＆ドロップ</p>
                    <button type="button" class="exeButton">ファイルを選択</button>
                </div>
            </div>
            <div class="imgSlotButtonArea">
                <button
                    type="button"
                    class="exeButton changeButton"
                    :disabled="!image.previewImg"
                    @click="openFileDialog(index)"
                >
                    <img src="../assets/userinfo_picticon.png" class="changeIcon" alt="">画像を変更
                </button>
                <button
                    type="button"
                    class="clearButton slotDeleteButton"
                    :disabled="!image.previewImg"
                    @click="deleteImage(index)"
                >削除</button>
            </div>
        </div>
    </div>
    <p class="textLeft note">・登録可能なファイル形式：JPG、PNG、WEBP</p>
    <p class="textLeft note">・推奨サイズ：640×640px 以上</p>
    <p class="textLeft note">・正面を向いた、はっきりと顔が映っている画像を登録してください。</p>
</template>

<style scoped>
    .imgSlotList {
        display: flex;
        gap: 16px;
        width: 100%;
        margin: 10px 0;
    }
    .imgSlot {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 10px;
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 10px;
        padding: 12px;
        box-sizing: border-box;
    }
    .imgSlotHeader {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .imgSlotTitle {
        font-weight: bold;
    }
    .imgSlotHeader .optionalView {
        padding: 2px 10px;
        border-radius: 6px;
        font-size: 14px;
        margin-left: 0;
    }
    .slotRequireView {
        color: white;
        background-color: #ff1493;
        padding: 2px 10px;
        border-radius: 6px;
        font-size: 14px;
        font-weight: bold;
    }
    .dropArea {
        flex: 1;
        border-radius: 10px;
        border: 2px dotted gray;
        padding: 20px 10px;
        cursor: pointer;
    }
    .dropInner img {
        width: 70px;
    }
    .mainText {
        font-size: 16px;
        font-weight: bold;
    }
    .subText {
        font-size: 14px;
        color: #777;
        margin: 5px;
    }
    .imgSlotPreview {
        flex: 1;
        min-height: 200px;
        cursor: pointer;
        background: #f3f3f3;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }
    .imgSlotPreview img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
    .imgSlotButtonArea {
        display: flex;
        gap: 8px;
    }
    .imgSlotButtonArea .changeButton {
        flex: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 8px 10px;
        width: auto;
        font-weight: bold;
    }
    .changeIcon {
        width: 20px;
        height: auto;
    }
    .imgSlotButtonArea .changeButton:disabled {
        color: #aaa;
        border-color: #ccc;
        cursor: not-allowed;
    }
    .imgSlotButtonArea .changeButton:disabled .changeIcon {
        filter: grayscale(1);
        opacity: 0.5;
    }
    .imgSlotButtonArea .slotDeleteButton {
        flex: 1;
        padding: 8px 10px;
        border: 1px solid #ccc;
        width: auto;
    }
    .imgSlotButtonArea .slotDeleteButton:disabled {
        color: #aaa;
        cursor: not-allowed;
    }
    .note {
        font-size: 15px;
        color: #777;
        margin-top: 5px;
    }

    @media (max-width: 1024px) {
        .imgSlotList {
            flex-direction: column;
        }
    }
    @media (max-width: 768px) {
        .note {
            font-size: 13px;
            line-height: 1.6;
        }
        .imgSlotPreview {
            height: 160px;
        }
    }
</style>
